

import React, { useEffect, useState } from 'react';
import { getAPI, postAPI } from '../../app/api';
import Loader from '../../UIComponents/Loader';
import { store } from '../../app/store';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { hasPermission, isAdmin } from '../../store/hooks/Permission';
// NOTE: For a production app, move these styles to a CSS/SCSS file and use classNames as in the theme.
// For this demo, we use inline styles and static data to match the theme's dashboard as closely as possible.

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const store_data = JSON.parse(store());
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [examStats, setExamStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [newParticipantsData, setNewParticipantsData] = useState([]);
  const [activeExamsDetail, setActiveExamsDetail] = useState([]);
  const [examWiseParticipants, setExamWiseParticipants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [results, setResults] = useState([]);

  useEffect(() => {
    async function checkPermissionsAndFetchData() {
      setLoading(true);
      
      // Check permissions first
      if (isAdmin()) {
        setCanView(true);
      } else {
        const viewAllowed = await hasPermission("GENERAL", "Dashboard", "VIEW");
        setCanView(viewAllowed);
        if (!viewAllowed) {
          setLoading(false);
          return;
        }
      }
      
      try {
        const org_id = store_data?.org_id;

        const [metricsRes, examStatsRes, dashboardStatsRes, monthlyRes, newParticipantsRes, activeExamsRes, examWiseRes, transactionsRes, resultsRes] = await Promise.all([
          getAPI('/dashboard/metrics'),
          postAPI('/exams/stats', {}),
          postAPI('/dashboard/get-stats', { org_id: org_id }),
          postAPI('/dashboard/get-monthly-recharge-withdrawal', { org_id: org_id }),
          postAPI('/dashboard/get-new-participants', { org_id: org_id }),
          postAPI('/dashboard/get-active-exams-detail', { org_id: org_id }),
          postAPI('/dashboard/get-exam-wise-participants', { org_id: org_id }),
          postAPI('/student-transactions/list', {}),
          postAPI('/results/list', {})
        ]);
        setMetrics(metricsRes?.data || {});
        setExamStats(examStatsRes?.data || {});
        setDashboardStats(dashboardStatsRes?.data || {});
        setMonthlyData(monthlyRes?.data || []);
        setNewParticipantsData(newParticipantsRes?.data || []);
        setActiveExamsDetail(activeExamsRes?.data || []);
        setExamWiseParticipants(examWiseRes?.data || []);
        // setPayments(paymentsRes?.data?.rows || []);
        setTransactions(transactionsRes?.data?.rows || []);
        setResults(resultsRes?.data?.rows || []);
      } catch (err) {
        // Optionally handle error
      }
      setLoading(false);
    }
    checkPermissionsAndFetchData();
  }, []);
  const now = new Date();

  const formatted = now.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (loading) return <Loader />;
  
  if (!canView) {
    return (
      <div style={{ background: '#f5f5f9', minHeight: '100vh', padding: '2rem 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <i className="fa fa-lock fa-3x text-danger mb-3"></i>
          <h4 className="text-danger">Access Denied</h4>
          <p className="text-muted">You do not have permission to view the dashboard.</p>
        </div>
      </div>
    );
  }

  // Example: Map API data to dashboard cards (replace static values)
  // You can further map/aggregate as needed for each card

  return (
    <div style={{ background: '#f5f5f9', minHeight: '100vh', padding: '2rem 0' }}>
      <div className="container-xxl flex-grow-1 container-p-y" style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem 0' }}>
          {/* Website Analytics Card */}
          <div className="col-lg-12 mb-2 bg-custom-gradient-primary">
            <div className="row position-relative">
              <div className="col-lg-10 p-4 rounded-3 text-white">
                <h4 className='text-white m-0'>Welcome, <b className='text-danger'>{store_data?.name}</b> 👋</h4>
                <span style={{ opacity: 0.7 }}>
                  It’s <b>{formatted}</b> — let’s turn plans into progress.
                  Your dashboard is ready with insights, updates, and tools to keep you moving forward. ✨
                </span>
              </div>
              <div className='position-absolute' style={{ right: '15px', bottom: '-24%', width: '200px' }}>
                <img src="/theme/assets/img/illustrations/giphy.gif" alt="Hello" style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
            {/* <h4 className="fw-bold py-3 mb-4">Welcome </h4> */}
          </div>

          {/* Dashboard Statistics Cards - 4 Cards in a Row */}
          {/* Card 1: Today's Total Enrolled Students */}
          <div className="col-lg-3 mb-2" style={{ flex: '0 0 25%', maxWidth: '25%', paddingRight: '1rem' }}>
            <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 12, color: '#fff', padding: 24, boxShadow: '0 2px 12px 0 rgba(102,126,234,0.2)', minHeight: 200 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Today's Enrolled Students</p>
                  <h3 style={{ margin: 0, fontSize: '32px', fontWeight: 700 }}>{dashboardStats?.today_enrolled_students || 0}</h3>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: 12, fontSize: 20 }}>
                  <i className="fa fa-user-plus"></i>
                </div>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12 }}>
                <small style={{ opacity: 0.8 }}>New registrations today</small>
              </div>
            </div>
          </div>

          {/* Card 2: Total Active Exams */}
          <div className="col-lg-3 mb-2" style={{ flex: '0 0 25%', maxWidth: '25%', paddingRight: '1rem' }}>
            <div style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: 12, color: '#fff', padding: 24, boxShadow: '0 2px 12px 0 rgba(245,87,108,0.2)', minHeight: 200 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Total Active Exams</p>
                  <h3 style={{ margin: 0, fontSize: '32px', fontWeight: 700 }}>{dashboardStats?.total_active_exams || 0}</h3>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: 12, fontSize: 20 }}>
                  <i className="fa fa-book"></i>
                </div>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12 }}>
                <small style={{ opacity: 0.8 }}>Exams running now</small>
              </div>
            </div>
          </div>

          {/* Card 3: Today's Total Recharge Amount */}
          <div className="col-lg-3 mb-2" style={{ flex: '0 0 25%', maxWidth: '25%', paddingRight: '1rem' }}>
            <div style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', borderRadius: 12, color: '#fff', padding: 24, boxShadow: '0 2px 12px 0 rgba(79,172,254,0.2)', minHeight: 200 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Today's Recharge Amount</p>
                  <h3 style={{ margin: 0, fontSize: '32px', fontWeight: 700 }}>₹{dashboardStats?.today_recharge_amount || '0.00'}</h3>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: 12, fontSize: 20 }}>
                  <i className="fa fa-wallet"></i>
                </div>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12 }}>
                <small style={{ opacity: 0.8 }}>Wallet recharges today</small>
              </div>
            </div>
          </div>

          {/* Card 4: Today's Total Withdrawal Request Amount */}
          <div className="col-lg-3 mb-2" style={{ flex: '0 0 25%', maxWidth: '25%' }}>
            <div style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', borderRadius: 12, color: '#fff', padding: 24, boxShadow: '0 2px 12px 0 rgba(250,112,154,0.2)', minHeight: 200 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Today's Withdrawal Requests</p>
                  <h3 style={{ margin: 0, fontSize: '32px', fontWeight: 700 }}>₹{dashboardStats?.today_withdrawal_amount || '0.00'}</h3>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: 12, fontSize: 20 }}>
                  <i className="fa fa-arrow-down"></i>
                </div>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12 }}>
                <small style={{ opacity: 0.8 }}>Pending withdrawals</small>
              </div>
            </div>
          </div>

          {/* Highlighted Total Income from Exams Card */}
          <div className="col-lg-12 mb-2">
            <div style={{
              background: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
              borderRadius: 16,
              color: '#fff',
              padding: 40,
              boxShadow: '0 8px 24px 0 rgba(255, 216, 155, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 180,
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              {/* Background accent */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '300px',
                height: '300px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%'
              }}></div>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, opacity: 0.95, letterSpacing: '0.5px' }}>TOTAL INCOME FROM EXAMS</p>
                    <h2 style={{ margin: 0, fontSize: '48px', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                      ₹{dashboardStats?.total_income_from_exams || '0.00'}
                    </h2>
                    <p style={{ margin: '12px 0 0 0', fontSize: '14px', opacity: 0.85 }}>
                      <i className="fa fa-check-circle me-2"></i>
                      All time cumulative revenue from exam enrollments
                    </p>
                  </div>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: 16,
                    padding: 24,
                    fontSize: 48,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <i className="fa fa-chart-line"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="col-lg-6 mb-4" style={{ flex: '0 0 50%', maxWidth: '50%' }}>
            <div style={{ background: 'linear-gradient(90deg, #7367f0 0%, #9e95f5 100%)', borderRadius: 16, color: '#fff', padding: 32, minHeight: 320, boxShadow: '0 2px 12px 0 rgba(115,103,240,0.07)' }}>
              <div className="row">
                <div className="col-12">
                  <h5 className="mb-0 mt-2" style={{ color: '#fff', fontWeight: 700 }}>Website Analytics</h5>
                  <small>Total Exams: {examStats?.total_exams || 0}, Enrolled: {examStats?.enrolled_exam || 0}</small>
                </div>
                <div className="row" style={{ marginTop: 24 }}>
                  <div className="col-lg-7 col-md-9 col-12 order-2 order-md-1" style={{ flex: '0 0 75%', maxWidth: '75%' }}>
                    <h6 className="mt-0 mt-md-3 mb-3" style={{ color: '#fff', fontWeight: 600 }}>Traffic</h6>
                    <div className="row">
                      <div className="col-6">
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                            <p style={{ margin: 0, fontWeight: 600, marginRight: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '2px 12px' }}>{metrics?.usersCount ?? 0}</p>
                            <p style={{ margin: 0 }}>Users</p>
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <p style={{ margin: 0, fontWeight: 600, marginRight: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '2px 12px' }}>{metrics?.employeesCount ?? 0}</p>
                            <p style={{ margin: 0 }}>Employees</p>
                          </li>
                        </ul>
                      </div>
                      <div className="col-6">
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                            <p style={{ margin: 0, fontWeight: 600, marginRight: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '2px 12px' }}>{metrics?.ordersCount ?? 0}</p>
                            <p style={{ margin: 0 }}>Exams</p>
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <p style={{ margin: 0, fontWeight: 600, marginRight: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '2px 12px' }}>{metrics?.revenue ? `₹${metrics.revenue}` : 0}</p>
                            <p style={{ margin: 0 }}>Revenue</p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-5 col-md-3 col-12 order-1 order-md-2 my-4 my-md-0 text-center" style={{ flex: '0 0 25%', maxWidth: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/theme/assets/img/illustrations/card-website-analytics-1.png" alt="Website Analytics" width={170} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginTop: '-150px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Monthly Recharge & Withdrawal Line Chart */}
          <div className="col-lg-6 mb-4">
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h6 style={{ marginBottom: 20, fontWeight: 700, color: '#333' }}>
                <i className="fa fa-line-chart me-2" style={{ color: '#7367f0' }}></i>
                Monthly Recharge & Withdrawal
              </h6>
              {monthlyData && monthlyData.length > 0 ? (
                <Line
                  data={{
                    labels: monthlyData.map(d => d.month),
                    datasets: [
                      {
                        label: 'Recharge Amount',
                        data: monthlyData.map(d => parseFloat(d.recharge)),
                        borderColor: '#4facfe',
                        backgroundColor: 'rgba(79, 172, 254, 0.1)',
                        borderWidth: 3,
                        pointRadius: 5,
                        pointBackgroundColor: '#4facfe',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        tension: 0.3
                      },
                      {
                        label: 'Withdrawal Amount',
                        data: monthlyData.map(d => parseFloat(d.withdrawal)),
                        borderColor: '#fa709a',
                        backgroundColor: 'rgba(250, 112, 154, 0.1)',
                        borderWidth: 3,
                        pointRadius: 5,
                        pointBackgroundColor: '#fa709a',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        tension: 0.3
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'top',
                        labels: { usePointStyle: true, padding: 15 }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { color: '#666' }
                      },
                      x: {
                        ticks: { color: '#666' }
                      }
                    }
                  }}
                  height={300}
                />
              ) : <p>No data available</p>}
            </div>
          </div>

          {/* New Participants Bar Chart */}
          <div className="col-lg-6 mb-4">
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h6 style={{ marginBottom: 20, fontWeight: 700, color: '#333' }}>
                <i className="fa fa-bar-chart me-2" style={{ color: '#f5576c' }}></i>
                New Participants (Last 30 Days)
              </h6>
              {newParticipantsData && newParticipantsData.length > 0 ? (
                <Bar
                  data={{
                    labels: newParticipantsData.map(d => d.date),
                    datasets: [
                      {
                        label: 'New Participants',
                        data: newParticipantsData.map(d => d.count),
                        backgroundColor: [
                          'rgba(115, 103, 240, 0.8)',
                          'rgba(240, 147, 251, 0.8)',
                          'rgba(79, 172, 254, 0.8)',
                          'rgba(250, 112, 154, 0.8)',
                          'rgba(255, 216, 155, 0.8)'
                        ],
                        borderRadius: 6,
                        borderSkipped: false
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        display: true,
                        labels: { usePointStyle: true, padding: 15 }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { color: '#666', stepSize: 1 }
                      },
                      x: {
                        ticks: { color: '#666' }
                      }
                    }
                  }}
                  height={300}
                />
              ) : <p>No data available</p>}
            </div>
          </div>

          {/* Active Exams Table (Participants, Transactions, Videos) */}
          <div className="col-lg-6 mb-4">
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h6 style={{ marginBottom: 20, fontWeight: 700, color: '#333' }}>
                <i className="fa fa-list me-2" style={{ color: '#7367f0' }}></i>
                Active Exams Overview
              </h6>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f9', borderBottom: '2px solid #e9e9f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333' }}>Exam Name</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333' }}>Participants</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333' }}>Videos</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeExamsDetail && activeExamsDetail.length > 0 ? (
                      activeExamsDetail.map((exam, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e9e9f0', ':hover': { backgroundColor: '#f9f9f9' } }}>
                          <td style={{ padding: '12px', color: '#333' }}>
                            <span style={{ fontWeight: 600 }}>{exam.exam_title}</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#7367f0',
                              color: '#fff',
                              padding: '4px 12px',
                              borderRadius: 20,
                              fontWeight: 600
                            }}>
                              {exam.participant_count}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#f5576c',
                              color: '#fff',
                              padding: '4px 12px',
                              borderRadius: 20,
                              fontWeight: 600
                            }}>
                              {exam.transaction_count}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <a href={`Setting/Exam/${exam.id}/Participants`} style={{
                              background: '#4facfe',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              <i className="fa fa-video"></i> Watch
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No active exams</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Exam Wise Participants Pie Chart */}
          <div className="col-lg-6 mb-4">
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h6 style={{ marginBottom: 20, fontWeight: 700, color: '#333' }}>
                <i className="fa fa-pie-chart me-2" style={{ color: '#f5576c' }}></i>
                Participants by Exam
              </h6>
              {examWiseParticipants && examWiseParticipants.length > 0 ? (
                <Pie
                  data={{
                    labels: examWiseParticipants.map(e => e.exam_title),
                    datasets: [
                      {
                        data: examWiseParticipants.map(e => e.participant_count),
                        backgroundColor: [
                          '#7367f0',
                          '#f093fb',
                          '#4facfe',
                          '#fa709a',
                          '#ffd89b',
                          '#667eea',
                          '#f5576c',
                          '#00f2fe',
                          '#fee140',
                          '#19547b',
                          '#764ba2',
                          '#4facfe'
                        ],
                        borderColor: '#fff',
                        borderWidth: 2
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          usePointStyle: true,
                          padding: 15,
                          font: { size: 12 }
                        }
                      }
                    }
                  }}
                  height={300}
                />
              ) : <p>No data available</p>}
            </div>
          </div>

          {/* ...existing code for other cards, but replace static values with API data as above... */}
        </div>
      </div>
    </div>
  );
}
